import {
  AuthWrapper,
  getCurrentFacilityId,
  useRbacToken,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import "@risalabs_frontend_org/auth-wrapper/styles.css";
import type { FirebaseOptions } from "firebase/app";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { firebaseApp } from "./api/firebase/firestoreService";
import AuthDataBootstrap from "./auth/AuthDataBootstrap";
import ErrorPage from "./components/errorPage/errorPage";
import { ApiBaseUrls } from "./enums/apiUrls";
import HomeVersion2 from "./pages/homeVersion2/homeVersion2";
import { RootState } from "./redux/store/store";
import { DefaultRouteConfig } from "./routes/defaultConfig";
import { ExternalPharmaPaRouteConfig } from "./routes/externalPharmaRoutes";
import { PharmaPaRouteConfig } from "./routes/pharmaPaRouteConfig";

/**
 * Resolve the auth-wrapper configuration from dashboard runtime environment values.
 *
 * @returns AuthWrapper configuration object used by the shared authentication shell
 */
const getAuthWrapperConfig = () => ({
  apiUrls: {
    authUrl: ApiBaseUrls.AUTH_URL,
    backendServiceUrl: ApiBaseUrls.BACKEND_SERVICE,
  },
  authRedirectPath: "/auth",
  bearerAuth: {
    password: process.env.REACT_APP_PASSWORD,
    username: process.env.REACT_APP_USERNAME,
  },
  firebaseConfig: firebaseApp.options as FirebaseOptions,
  version: process.env.REACT_APP_VERSION,
});

/**
 * Resolve route configuration based on whether an authenticated user is present.
 *
 * @param hasAuthenticatedUser - Whether an authenticated user exists in Redux auth slice
 * @returns Route config array for the current user's context
 */

type AnyRoute = {
  index?: boolean;
  path?: string;
  element?: React.ReactNode;
  children?: AnyRoute[];
};

/**
 * Recursively convert a RouteObject[] (data-router shape) into JSX <Route> elements
 * suitable for use inside <Routes>.
 *
 * @param routes - Route definitions in object form
 * @returns Array of JSX <Route> nodes
 */
const renderRouteObjects = (routes: AnyRoute[]): React.ReactNode[] =>
  routes.map((route, index) => {
    const key =
      route.path ?? (route.index ? `index-${index}` : `route-${index}`);

    if (route.index) {
      return <Route key={key} index element={route.element} />;
    }

    return (
      <Route key={key} path={route.path} element={route.element}>
        {route.children ? renderRouteObjects(route.children) : null}
      </Route>
    );
  });

/**
 * Bootstrap the pharmacy dashboard using the shared auth-wrapper shell.
 *
 * Mirrors claims-dashboard structure: BrowserRouter > AuthWrapper >
 * AuthDataBootstrap > Routes with HomeVersion2 as the layout route element.
 * Auth gating is handled inside AuthWrapper; do not wrap routes in ProtectRoute.
 *
 * @returns The dashboard root wrapped by authentication providers
 */
const App = () => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const [routeConfig, setRouteConfig] = useState<any>(DefaultRouteConfig);
  const [isOrgSetupComplete, setIsOrgSetupComplete] = useState(false);
  const { isLoaded: isRbacTokenLoaded } = useRbacToken(
    providerDetails?.FacilityId,
  );

  const authWrapperConfig = useMemo(() => getAuthWrapperConfig(), []);

  const determineRouteConfig = () => {
    const currentFacilityId = getCurrentFacilityId() ?? "";
    if (!currentFacilityId) {
      return PharmaPaRouteConfig;
    }

    const facilityType = userLoggedInFacilityType(currentFacilityId);

    switch (facilityType) {
      case "external":
        return ExternalPharmaPaRouteConfig;
      case "internal":
        return PharmaPaRouteConfig;
      case "not_assigned":
      default:
        return PharmaPaRouteConfig;
    }
  };

  useEffect(() => {
    if (!user) {
      setRouteConfig(DefaultRouteConfig);
      return;
    }

    if (!isRbacTokenLoaded) {
      return;
    }

    const setupRouteConfig = () => {
      const newRouteConfig = determineRouteConfig();

      if (newRouteConfig === PharmaPaRouteConfig && !isOrgSetupComplete) {
        setIsOrgSetupComplete(true);
      }

      setRouteConfig(newRouteConfig);
    };

    setupRouteConfig();
  }, [user, isRbacTokenLoaded]);

  return (
    <BrowserRouter>
      <AuthWrapper config={authWrapperConfig}>
        <AuthDataBootstrap>
          <Routes>
            <Route path="/" element={<HomeVersion2 />}>
              {renderRouteObjects(routeConfig)}
            </Route>
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </AuthDataBootstrap>
      </AuthWrapper>
    </BrowserRouter>
  );
};

export default App;
